(function (window, document) {
  // Grab pixleeApi configuration from document meta tags.
  const pixleeApi = window.captureMetadata('pixleeApi');
  // If no Pixlee API config is found, do nothing.
  if (!pixleeApi) return;

  init();

  if (window.componentEvents) {
    componentEvents.on('component-init', init);
  }

  /**
   * Instantiate all Pixlee galleries on the page.
   */
  function init() {
    // Find all gallery containers on the page.
    const containers = document.querySelectorAll('[data-pixlee-widget]:not([data-pixlee-widget-init="true"])');
    // If no uninstantiated gallery containers are found, do nothing.
    if (!containers.length) return;
    // Create global function to be called after page load, which will
    // immediately instantiate all async Pixlee widgets and watch for
    // lazy-loaded widgets to be instantiated later. Pixlee looks for this
    // PixleeAsyncInit function and runs it when it is good and ready.
    window.PixleeAsyncInit = pixleeAsyncInitFactory(containers);
    // Create and inject external script.
    injectExternalScript();
  }

  /**
   * Inject external gallery provider script and return a reference.
   */
  function injectExternalScript() {
    const src = 'https://assets.pxlecdn.com/assets/pixlee_widget_1_0_0.js';
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = src;
    document.body.appendChild(script);
  }

  /**
   * Generate Pixlee instantiation function using array of containers.
   */
  function pixleeAsyncInitFactory(containers) {
    // Separate lazy containers from async containers.
    const { asyncContainers, lazyContainers } = Array.from(containers)
      .reduce((results, container) => {
        if (container.dataset.pixleeLazyLoad === 'true') {
          results.lazyContainers.push(container);
        } else {
          results.asyncContainers.push(container);
        }
        return results;
      }, { asyncContainers: [], lazyContainers: [] });
    return function PixleeAsyncInit() {
      Pixlee.init({apiKey: pixleeApi.key});
      // Add a widget for each async container.
      asyncContainers.forEach(addPixleeWidget);
      // Create intersection observer to watch for lazy containers.
      watchLazyLoadedPixleeWidgets(lazyContainers);
    }
  }

  /**
   * Create an intersection observer which will instantiate each lazy-loaded
   * Pixlee widget as the user scrolls close to it.
   */
  function watchLazyLoadedPixleeWidgets(containers) {
    const observer = new IntersectionObserver(lazyLoadPixleeWidget, {
      rootMargin: '100px',
      threshold: 0,
    });
    containers.forEach(container => observer.observe(container));
  }

  /**
   * Lazy-load Pixlee widgets whose containers intersect with the viewport.
   */
  function lazyLoadPixleeWidget(entries, observer) {
    entries.forEach(entry => {
      // If entry is not currently intersecting, do nothing.
      if (!entry.isIntersecting) return;
      // Since entry _is_ intersecting, add the Pixlee widget.
      addPixleeWidget(entry.target);
      // When lazy-loading widgets, it is necessary to invoke the
      // resizeWidget() method to “ensure the iframe resizes properly with
      // respect to the content around it.”
      // See: https://developers.pixlee.com/docs/pixlee-widget-user-guide#single-page-applications
      Pixlee.resizeWidget();
      // Unobserve the container, since there is nothing more to do with it.
      observer.unobserve(entry.target);
    });
  }

  /**
   * Create and configure Pixlee widget for a single container.
   */
  function addPixleeWidget(container) {
    const config = createPixleeWidgetConfig(container);
    if (config.skuId) {
      // If there is a skuId in the config, add a product widget.
      Pixlee.addProductWidget(config);
    } else if (config.categoryId) {
      // If there is a categoryId in the config, add a category widget.
      Pixlee.addCategoryWidget(config);
    } else {
      // Add a simple widget as a fallback.
      Pixlee.addSimpleWidget(config);
    }
    container.dataset.pixleeWidgetInit = true;
  }

  /**
   * Create a configuration object for a Pixlee widget, given a container
   * element’s attributes.
   */
  function createPixleeWidgetConfig(container) {
    const config = {
      // Explicitly set the container’s ID as Pixlee’s containerId to guarantee
      // the correct container is targeted.
      containerId: container.id,
    };
    const {
      accountId,
      apiKey,
      categoryId,
      fixedWidth,
      lightbox,
      styleId,
      subscribedEvents,
      widgetId
    } = container.dataset;
    // Pixlee expects accountId to be an integer.
    if (accountId) config.accountId = parseInt(accountId);
    // Pixlee expects categoryId to be an integer.
    if (categoryId) config.categoryId = parseInt(categoryId);
    // Pixlee expects fixedWidth to be a boolean.
    if (fixedWidth) config.fixedWidth = fixedWidth === 'true';
    // Pixlee expects lightbox to be a boolean.
    if (lightbox) config.lightbox = lightbox === 'true';
    // Pixlee expects a skuId, but Maui Jim passes a style ID.
    if (styleId) config.skuId = styleId;
    // Pixlee expects subscribedEvents to be an array of strings. The data
    // attribute should contain a string of comma-separated event names.
    if (subscribedEvents) config.subscribedEvents = subscribedEvents.split(',')
      // Trim the resulting strings in case spaces were added between events.
      .map((event) => event.trim());
    // Pixlee expects widgetId to be an integer.
    if (widgetId) config.widgetId = parseInt(widgetId);
    return config;
  }
})(window, document);
